<script lang="ts">
  import { onMount } from 'svelte';
  import type {
    DevicePoolMemberSummary,
    DevicePoolSummary,
    IngressConnectionSummary,
    UnassignedDeviceCandidate,
  } from '@ucc/shared';
  import {
    ApiError,
    assignDeviceToPool,
    fetchDevicePool,
    fetchDevicePoolDevices,
    fetchIngressConnections,
    fetchUnassignedDeviceCandidates,
    updateDevicePool,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let { poolId }: { poolId: string } = $props();

  let pool = $state<DevicePoolSummary | null>(null);
  let connections = $state<IngressConnectionSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  // Edit form
  let editing = $state(false);
  let editName = $state('');
  let editDescription = $state('');
  let editConnectionId = $state('');
  let saving = $state(false);
  let editError = $state<string | null>(null);

  // Members table
  let members = $state<DevicePoolMemberSummary[]>([]);
  let membersTotal = $state(0);
  let membersPage = $state(1);
  const pageSize = 50;
  let memberSearch = $state('');
  let membersLoading = $state(false);
  let membersError = $state<string | null>(null);

  // Add device
  let addQuery = $state('');
  let addResults = $state<UnassignedDeviceCandidate[]>([]);
  let addSearching = $state(false);
  let addError = $state<string | null>(null);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');
  const editConnection = $derived(connections.find((c) => c.id === editConnectionId) ?? null);
  const totalPages = $derived(Math.max(1, Math.ceil(membersTotal / pageSize)));

  onMount(() => {
    void loadPool();
    void loadConnections();
    void loadMembers();
  });

  async function loadPool() {
    loading = true;
    error = null;
    try {
      const res = await fetchDevicePool(poolId);
      pool = res.pool;
      startEdit();
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load Device Pool';
    } finally {
      loading = false;
    }
  }

  async function loadConnections() {
    try {
      const res = await fetchIngressConnections();
      connections = res.connections;
    } catch {
      // non-fatal — edit form will just show an empty connection list
    }
  }

  async function loadMembers() {
    membersLoading = true;
    membersError = null;
    try {
      const res = await fetchDevicePoolDevices(poolId, { search: memberSearch, page: membersPage, pageSize });
      members = res.devices;
      membersTotal = res.total;
    } catch (err) {
      membersError = err instanceof ApiError ? err.message : 'Failed to load devices';
    } finally {
      membersLoading = false;
    }
  }

  function startEdit() {
    if (!pool) return;
    editName = pool.name;
    editDescription = pool.description ?? '';
    editConnectionId = pool.connectionId ?? '';
    editError = null;
  }

  function onSearchInput() {
    membersPage = 1;
    void loadMembers();
  }

  function changePage(delta: number) {
    const next = membersPage + delta;
    if (next < 1 || next > totalPages) return;
    membersPage = next;
    void loadMembers();
  }

  async function onSaveEdit(e: Event) {
    e.preventDefault();
    editError = null;
    const name = editName.trim();
    if (!name) {
      editError = 'Name is required.';
      return;
    }
    saving = true;
    try {
      const res = await updateDevicePool(poolId, {
        name,
        description: editDescription.trim() || null,
        connectionId: editConnectionId || null,
      });
      pool = res.pool;
      editing = false;
    } catch (err) {
      editError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      saving = false;
    }
  }

  let addSearchTimer: ReturnType<typeof setTimeout> | undefined;
  function onAddQueryInput() {
    addError = null;
    if (addSearchTimer) clearTimeout(addSearchTimer);
    const q = addQuery.trim();
    if (!q) {
      addResults = [];
      return;
    }
    addSearchTimer = setTimeout(() => void runAddSearch(q), 250);
  }

  async function runAddSearch(q: string) {
    addSearching = true;
    try {
      const res = await fetchUnassignedDeviceCandidates(q);
      addResults = res.devices;
    } catch (err) {
      addError = err instanceof ApiError ? err.message : 'Search failed';
    } finally {
      addSearching = false;
    }
  }

  async function onAddDevice(deviceId: string) {
    addError = null;
    try {
      await assignDeviceToPool(deviceId, { devicePoolId: poolId });
      addQuery = '';
      addResults = [];
      await loadMembers();
      await loadPool();
    } catch (err) {
      addError = err instanceof ApiError ? err.message : 'Add failed';
    }
  }

  async function onRemoveDevice(deviceId: string) {
    membersError = null;
    try {
      await assignDeviceToPool(deviceId, { devicePoolId: null });
      await loadMembers();
      await loadPool();
    } catch (err) {
      membersError = err instanceof ApiError ? err.message : 'Remove failed';
    }
  }
</script>

<div class="page">
  <a class="back" href="#/device-pools">← Device Pool</a>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can manage Device Pools.</p>
    </Card>
  {:else if loading}
    <p class="muted">Loading…</p>
  {:else if error}
    <p class="err" role="alert">{error}</p>
  {:else if pool}
    <h2>{pool.name}</h2>

    <Card title="Pool settings" subtitle={editing ? undefined : 'Click Edit to change connection'}>
      {#if !editing}
        <dl class="detail-grid">
          <dt>Description</dt>
          <dd>{pool.description || '—'}</dd>
          <dt>Connection</dt>
          <dd>{connections.find((c) => c.id === pool!.connectionId)?.name ?? '—'}</dd>
        </dl>
        <div class="actions">
          <button type="button" class="linkish" onclick={() => (editing = true)}>Edit</button>
        </div>
      {:else}
        <form class="form" onsubmit={(e) => void onSaveEdit(e)}>
          <label class="field">
            <span class="label">Name</span>
            <input class="input" type="text" maxlength={120} bind:value={editName} disabled={saving} required />
          </label>
          <label class="field">
            <span class="label">Description</span>
            <input class="input" type="text" bind:value={editDescription} disabled={saving} />
          </label>
          <label class="field">
            <span class="label">Connection</span>
            <select class="input" bind:value={editConnectionId} disabled={saving}>
              <option value="">(none)</option>
              {#each connections as c (c.id)}
                <option value={c.id}>{c.name} ({c.type})</option>
              {/each}
            </select>
          </label>
          {#if editConnection && editConnection.type !== 'https'}
            <p class="field-hint">
              MQTT/MQTTS topics are fixed per device (derived from its UID) — nothing to set here.
            </p>
          {/if}
          {#if editError}
            <p class="err" role="alert">{editError}</p>
          {/if}
          <div class="actions">
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            <button
              type="button"
              class="linkish muted"
              disabled={saving}
              onclick={() => {
                editing = false;
                startEdit();
              }}>Cancel</button
            >
          </div>
        </form>
      {/if}
    </Card>

    <Card title="Add a device" subtitle="Search shows unassigned devices only">
      <div class="form">
        <input
          class="input"
          type="text"
          placeholder="Search UID or name…"
          bind:value={addQuery}
          oninput={onAddQueryInput}
        />
        {#if addError}
          <p class="err" role="alert">{addError}</p>
        {/if}
        {#if addSearching}
          <p class="muted small">Searching…</p>
        {:else if addQuery.trim() && addResults.length === 0}
          <p class="muted small">No unassigned devices match.</p>
        {:else if addResults.length > 0}
          <ul class="candidate-list">
            {#each addResults as d (d.id)}
              <li>
                <span class="mono">{d.uid}</span> — {d.name}
                <button type="button" class="linkish" onclick={() => void onAddDevice(d.id)}>Add</button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    </Card>

    <Card title="Devices in this pool" subtitle={`${membersTotal} device(s)`}>
      <input
        class="input"
        type="text"
        placeholder="Search UID or name…"
        bind:value={memberSearch}
        oninput={onSearchInput}
      />
      {#if membersError}
        <p class="err" role="alert">{membersError}</p>
      {/if}
      {#if membersLoading}
        <p class="muted">Loading…</p>
      {:else if members.length === 0}
        <p class="muted">No devices in this pool{memberSearch ? ' matching your search' : ''}.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>UID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each members as m (m.id)}
                <tr>
                  <td class="mono">{m.uid}</td>
                  <td>{m.name}</td>
                  <td>{m.status}</td>
                  <td class="mono small">{m.lastSeenAt ? new Date(m.lastSeenAt).toLocaleString() : '—'}</td>
                  <td class="row-actions">
                    <button type="button" class="linkish" onclick={() => void onRemoveDevice(m.id)}>Remove</button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        <div class="pager">
          <button type="button" class="linkish" disabled={membersPage <= 1} onclick={() => changePage(-1)}
            >← Prev</button
          >
          <span class="muted small">Page {membersPage} of {totalPages}</span>
          <button type="button" class="linkish" disabled={membersPage >= totalPages} onclick={() => changePage(1)}
            >Next →</button
          >
        </div>
      {/if}
    </Card>
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
    width: 100%;
    box-sizing: border-box;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .small {
    font-size: 0.8rem;
  }
  .field-hint {
    font-size: 0.8rem;
    color: var(--text-muted);
    margin: 0;
  }
  .detail-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.35rem 1rem;
    margin: 0;
  }
  .detail-grid dt {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
  .detail-grid dd {
    margin: 0;
  }
  .table-wrap {
    overflow-x: auto;
    margin-top: 0.75rem;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
  }
  .linkish:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .linkish.muted {
    color: var(--text-muted);
  }
  .candidate-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .candidate-list li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    border-radius: 0.4rem;
  }
  .pager {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
</style>
